import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompanyServiceCoinLimitItem {
  serviceCoinId: string;
  coinId: string;
  minPerOperation?: number | null;
  maxPerOperation?: number | null;
  maxDaily?: number | null;
  openCloseDiff?: number | null;
}

export interface CompanyServiceCoinLimitCoinResponse {
  id: string;
  coinId: string;
  coinCode?: string;
  coinName?: string;
  minPerOperation?: number | null;
  maxPerOperation?: number | null;
  maxDaily?: number | null;
  openCloseDiff?: number | null;
}

export interface CompanyServiceCoinLimitGroupResponse {
  serviceCoinId: string;
  serviceCoinCode?: string;
  serviceCoinName?: string;
  coins: CompanyServiceCoinLimitCoinResponse[];
}

export interface CompanyServiceCoinLimits {
  companyId: string;
  serviceId: string;
  groups: CompanyServiceCoinLimitGroupResponse[];
}

export interface CompanyServiceCoinLimitsUpsertPayload {
  items: CompanyServiceCoinLimitItem[];
}

/**
 * Monedas y límites por servicio de empresa (drawer «Monedas y límites»).
 *
 * GET/PUT `/api/structure/companies/{companyId}/services/{serviceId}/coin-limits`
 */
@Injectable({ providedIn: 'root' })
export class CompanyServiceCoinLimitsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private companiesUrl(companyId: string, serviceId: string): string {
    return `${this.apiConfig.apiUrl}/structure/companies/${companyId}/services/${serviceId}/coin-limits`;
  }

  get(companyId: string, serviceId: string): Observable<CompanyServiceCoinLimits> {
    return this.httpClient
      .get<ApiResponse<CompanyServiceCoinLimits>>(
        this.companiesUrl(companyId, serviceId),
      )
      .pipe(map((response) => this.normalize(response.data, companyId, serviceId)));
  }

  update(
    companyId: string,
    serviceId: string,
    payload: CompanyServiceCoinLimitsUpsertPayload,
  ): Observable<CompanyServiceCoinLimits> {
    return this.httpClient
      .put<ApiResponse<CompanyServiceCoinLimits>>(
        this.companiesUrl(companyId, serviceId),
        { items: payload.items ?? [] },
      )
      .pipe(
        map((response) =>
          this.normalize(response.data, companyId, serviceId),
        ),
      );
  }

  private normalize(
    data: CompanyServiceCoinLimits | undefined,
    companyId: string,
    serviceId: string,
  ): CompanyServiceCoinLimits {
    if (data?.groups) {
      return {
        companyId: data.companyId || companyId,
        serviceId: data.serviceId || serviceId,
        groups: data.groups.map((group) => ({
          serviceCoinId: group.serviceCoinId,
          serviceCoinCode: group.serviceCoinCode,
          serviceCoinName: group.serviceCoinName,
          coins: Array.isArray(group.coins) ? group.coins : [],
        })),
      };
    }
    return {
      companyId,
      serviceId,
      groups: [],
    };
  }
}
